import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SYNC_PROVIDER, retryChange, syncNow, syncSummary, useSync } from '../services/sync';
import { LEADS_SOURCE, refreshLeads, useLeadsLoad } from '../services/leads';
import { CATALOG_SOURCE, refreshCatalog, useCatalog } from '../services/catalog';
import { refreshDrafts } from '../services/drafts';
import './SyncStatus.css';

/**
 * Top-bar Zoho sync indicator — two directions:
 *   from Zoho: are the appointments up to date? (last updated, ↻ Refresh now)
 *   to Zoho:   is what staff did in the app actually in Zoho? (recent changes, errors, retry)
 */

const LABEL = {
  synced: () => '✓ Synced',
  syncing: (n) => `⟳ Syncing ${n}`,
  offline: (n) => `⏸ Offline · ${n} waiting`,
  failed: (n) => `⚠ ${n} not synced`,
};

const STATUS_TEXT = {
  queued: 'Waiting',
  syncing: 'Syncing…',
  synced: 'In Zoho',
  failed: 'Not synced',
};

// test mode must never claim something is in Zoho
const statusText = (status, simulated) => (simulated && status === 'synced' ? 'Synced (simulated)' : STATUS_TEXT[status]);

const time = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// other pages (the dashboard) can open the panel
const OPEN_EVENT = 'cie:open-sync';
export const openSyncPanel = () => window.dispatchEvent(new Event(OPEN_EVENT));

const SyncStatus = () => {
  const sync = useSync();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);
  const load = useLeadsLoad();
  const catalog = useCatalog();
  const fromZoho = LEADS_SOURCE === 'zoho';
  const { state, count } = syncSummary(sync);
  const test = SYNC_PROVIDER !== 'api';
  // the pill shows the worst of the two: loading from Zoho failed / is loading, else the outgoing changes
  const pill =
    fromZoho && load.status === 'error'
      ? { tone: 'failed', text: '⚠ Zoho not reached' }
      : fromZoho && load.status === 'loading'
      ? { tone: 'syncing', text: '⟳ Syncing' }
      : { tone: state, text: LABEL[state](count) };
  const updatedAt = load.fetchedAt && new Date(load.fetchedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const recent = [...sync.changes].reverse();

  return (
    <>
      <button type="button" className={`sync-pill ${pill.tone}`} onClick={() => setOpen(true)} aria-label="Zoho sync status">
        {pill.text}
        {test && !fromZoho && <span className="sync-test">TEST</span>}
      </button>

      {/* rendered at page level — the top bar's blur would otherwise trap the panel inside it */}
      {open && createPortal(
        <div className="sync-overlay" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="sync-panel" role="dialog" aria-label="Zoho sync">
            <div className="sync-panel-head">
              <div>
                <h3>Zoho sync</h3>
                <p className={`sync-connection ${test ? 'test' : 'live'}`}>
                  {test && fromZoho
                    ? 'Appointments and their status changes (cancel, rebook, reschedule, report sent, notes) are live with Zoho. Follow-ups, quotes and bookings from Calls are still test — simulated.'
                    : test
                    ? 'Test mode — Zoho isn’t connected yet. Changes are queued and sync is simulated. Test leads and follow-ups reset when the app reloads; once Zoho is connected, Zoho keeps them.'
                    : 'Connected — changes are written to Zoho CRM.'}
                </p>
              </div>
              <button type="button" className="sync-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>
            </div>

            {/* from Zoho: the appointments */}
            {fromZoho && (
              <div className="sync-section">
                <div className="sync-section-title">Appointments from Zoho</div>
                <div className={`sync-summary ${load.status === 'error' ? 'failed' : load.status === 'ready' ? 'synced' : 'syncing'}`}>
                  {load.status === 'ready' && `✓ Up to date · ${load.count} appointment${load.count === 1 ? '' : 's'} · updated ${updatedAt}`}
                  {(load.status === 'loading' || load.status === 'idle') && '⟳ Loading appointments from Zoho…'}
                  {load.status === 'error' && `⚠ ${load.error}${updatedAt ? ` Showing what loaded at ${updatedAt}.` : ''}`}
                </div>
                {CATALOG_SOURCE === 'zoho' && (
                  <div className={`sync-summary ${catalog.error ? 'failed' : 'synced'}`}>
                    {catalog.error
                      ? `⚠ Service catalog: ${catalog.error}`
                      : catalog.items.length
                        ? `✓ Service catalog · ${catalog.items.length} services`
                        : '⟳ Loading the service catalog…'}
                  </div>
                )}
                <button
                  type="button"
                  className="sync-refresh"
                  onClick={() => {
                    refreshLeads();
                    refreshCatalog();
                    refreshDrafts();
                  }}
                  disabled={load.status === 'loading'}
                >
                  {load.status === 'loading' ? 'Refreshing…' : '↻ Refresh now'}
                </button>
              </div>
            )}

            {fromZoho && <div className="sync-section-title sync-section-gap">Changes from the app</div>}
            <div className={`sync-summary ${state}`}>
              {state === 'synced' && (test ? 'Everything is synced — simulated until Zoho is connected.' : 'Everything you’ve done is in Zoho.')}
              {state === 'syncing' && `${count} change${count > 1 ? 's' : ''} on the way to Zoho.`}
              {state === 'offline' && `No connection. ${count} change${count > 1 ? 's' : ''} saved on this phone — they’ll sync when you’re back online.`}
              {state === 'failed' && `${count} change${count > 1 ? 's' : ''} didn’t reach Zoho. They retry automatically, or tap Retry.`}
            </div>

            <div className="sync-list">
              {recent.length === 0 && <p className="sync-empty">No changes yet.</p>}
              {recent.map((c) => (
                <div key={c.id} className={`sync-item ${c.status}`}>
                  <span className="sync-dot" />
                  <div className="sync-item-main">
                    <div className="sync-item-label">{c.label}</div>
                    <div className="sync-item-meta">
                      {statusText(c.status, test && !c.live)} · {time(c.syncedAt || c.createdAt)}
                      {c.error ? ` · ${c.error}` : ''}
                    </div>
                  </div>
                  {c.status === 'failed' && (
                    <button type="button" className="sync-retry" onClick={() => retryChange(c.id)}>Retry</button>
                  )}
                </div>
              ))}
            </div>

            <div className="sync-foot">
              <button type="button" className="sync-now" onClick={syncNow} disabled={state === 'synced' || state === 'offline'}>
                Sync now
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SyncStatus;
